import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePointerActivation } from '../hooks/usePointerActivation'
import type { ItemReportReason, ReportHandler } from '../types'

type ReportStatus = 'idle' | 'submitting' | 'done' | 'error'

export interface ReportItemButtonProps {
  readonly itemId: string
  /**
   * Motif envoyé au serveur. Le tap reste unique — pas de sélecteur qui
   * ralentirait le geste — la mécanique appelante choisit le motif le plus
   * pertinent pour son contexte (ex. `wrong_answer` pendant une correction).
   */
  readonly reason?: ItemReportReason
  readonly onReport: ReportHandler
}

/**
 * Signale un item douteux en un tap (SPEC §11.2), via `usePointerActivation`
 * (`pointerdown` + `click` + verrou anti-double-envoi — voir ce hook pour le
 * détail du motif).
 */
export function ReportItemButton({ itemId, reason = 'other', onReport }: ReportItemButtonProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<ReportStatus>('idle')
  const isDisabled = status === 'submitting' || status === 'done'

  const activate = useCallback(
    (reset: () => void) => {
      setStatus('submitting')

      void (async () => {
        try {
          await onReport(itemId, reason)
          setStatus('done')
        } catch {
          reset()
          setStatus('error')
        }
      })()
    },
    [itemId, reason, onReport],
  )

  const { onPointerDown, onClick } = usePointerActivation(activate, isDisabled)

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onPointerDown={onPointerDown}
        onClick={onClick}
        disabled={isDisabled}
        aria-pressed={status === 'done'}
        className={[
          'inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors disabled:cursor-not-allowed',
          status === 'done'
            ? 'bg-surface-container text-outline'
            : 'bg-surface-low text-on-surface-variant hover:bg-error/10 hover:text-error',
        ].join(' ')}
      >
        <span aria-hidden="true">🚩</span>
        <span>{status === 'done' ? t('practice.report.done') : t('practice.report.action')}</span>
      </button>

      {status === 'error' && (
        <p role="alert" className="text-[11px] font-medium text-error">
          {t('practice.report.error')}
        </p>
      )}
    </div>
  )
}
